import { FC, useEffect, useState } from 'react';
import {
  FontAwesomeIconProps,
  FontAwesomeIcon,
} from '@fortawesome/react-fontawesome';
import { Flex, Button, BoxV2 as Box } from 'portal-commons';
import { useForm, FormProvider, FieldValues } from 'react-hook-form';
import { Dialog, DialogContent } from '@material-ui/core';

import { RcsCampaign } from '../../types';
import { Modal } from '@/components';

interface CampaignDetailsEditModalProps {
  open: boolean;
  title: string;
  icon: FontAwesomeIconProps['icon'];
  onCancel: () => void;
  onSave: (data: Partial<RcsCampaign>) => void;
  disabled?: boolean;
}

const CampaignDetailsEditModal: FC<CampaignDetailsEditModalProps> = ({
  open,
  onCancel,
  onSave,
  disabled,
  title,
  icon,
  children,
}) => {
  const formMethods = useForm({
    mode: 'all',
  });

  const [isReVerifyModalOpen, setIsReVerifyModalOpen] = useState(false);

  useEffect(() => {
    if (!open) {
      formMethods.clearErrors();
      formMethods.reset({});
      setIsReVerifyModalOpen(false);
    }
  }, [open]);

  const isNeedReVerify = () => {
    const { dirtyFields } = formMethods.formState;
    return dirtyFields.banner || dirtyFields.logo;
  };

  const handleFormSubmit = (data: FieldValues) => {
    if (isNeedReVerify()) {
      setIsReVerifyModalOpen(true);
    } else {
      onSave(fieldArrayTransform(data));
    }
  };

  const fieldArrayTransform = (data: FieldValues) => {
    const {
      websites,
      websiteLabels,
      phones,
      phoneLabels,
      emails,
      emailLabels,
      ...restData
    } = data;

    const apiPayload: Partial<RcsCampaign> = {
      ...restData,
      // Transform the array of objects into an array of strings
      ...(websites && {
        websites: websites.map((item: { value: string }) => item.value),
      }),
      ...(websiteLabels && {
        websiteLabels: websiteLabels.map(
          (item: { value: string }) => item.value
        ),
      }),
      ...(phones && {
        phones: phones.map((item: { value: string }) => item.value),
      }),
      ...(phoneLabels && {
        phoneLabels: phoneLabels.map((item: { value: string }) => item.value),
      }),
      ...(emails && {
        emails: emails.map((item: { value: string }) => item.value),
      }),
      ...(emailLabels && {
        emailLabels: emailLabels.map((item: { value: string }) => item.value),
      }),
    };
    return apiPayload as Partial<RcsCampaign>;
  };

  const handleConfirmReVerify = () => {
    const payload = fieldArrayTransform(formMethods.getValues());
    onSave(payload);
  };

  const hasErrorMessage = Object.values(formMethods.formState.errors).some(
    (error: any) => {
      if (Array.isArray(error)) {
        return error.some((err) => err?.value?.message);
      }
      return !!error?.message;
    }
  );

  return (
    <>
      <Dialog
        BackdropProps={{
          style: {
            backdropFilter: 'blur(1px)',
          },
        }}
        open={open}
        maxWidth="md"
        fullWidth
        data-testid="campaignDetailsEditModal"
      >
        <DialogContent>
          <Flex
            as="h3"
            sx={{
              alignItems: 'center',
              gap: 'xs',
              color: 't.black80',
              flexDirection: 'row',
              fontSize: '24px',
              fontWeight: 'semiBold',
              mt: 'xl',
              mb: 'xxl',
              justifyContent: 'center',
            }}
          >
            <FontAwesomeIcon icon={icon} fontSize="24" />
            {title}
          </Flex>
          <FormProvider {...formMethods}>
            <form onSubmit={formMethods.handleSubmit(handleFormSubmit)}>
              {children}
              <Flex
                sx={{
                  columnGap: '18px',
                  padding: '30px 0',
                  flexDirection: 'row',
                  justifyContent: 'center',
                  gap: 'l',
                }}
              >
                <Button
                  variant="outline"
                  onClick={onCancel}
                  data-testid="editModalCancelButton"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={disabled || hasErrorMessage}
                  data-testid="editModalSaveButton"
                >
                  Save
                </Button>
              </Flex>
            </form>
          </FormProvider>
        </DialogContent>
      </Dialog>
      <Modal open={isReVerifyModalOpen} testId="reVerifyModal">
        <Box
          as="h3"
          sx={{
            color: 't.black80',
            fontSize: '24px',
            fontWeight: 'semiBold',
          }}
        >
          Re-Verification
        </Box>
        <Box
          sx={{
            fontSize: 'H400',
            color: 't.black80',
            lineHeight: 'normal',
            mt: 'l',
            mb: 'm',
          }}
        >
          Changing campaign details will require the re-verification of a
          campaign, the status of the campaign will change and it will be
          resubmitted for verification. Click to Confirm.
        </Box>
        <Flex
          sx={{
            columnGap: '18px',
            padding: '30px 0',
            flexDirection: 'row',
            justifyContent: 'center',
            gap: 'l',
          }}
        >
          <Button
            variant="outline"
            onClick={() => setIsReVerifyModalOpen(false)}
            data-testid="reVerifyModalCancelButton"
          >
            Cancel
          </Button>
          <Button
            disabled={disabled}
            onClick={handleConfirmReVerify}
            data-testid="reVerifyModalConfirmButton"
          >
            Confirm
          </Button>
        </Flex>
      </Modal>
    </>
  );
};

export default CampaignDetailsEditModal;
